import { getStore } from 'cadenza/store/cadenza-store';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorkbookLayerDto } from 'cadenza/workbook/api/workbook-layer-dto';
import type { WorkbookLayerTreeNode } from 'cadenza/workbook/api/workbook-layer-tree-node';
import { isDataViewLayer } from 'cadenza/workbook/map/workbook-layer-type-util';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id';
import { LayerSettingsKey } from 'cadenza/workbook/model/layer-settings-key';
import type { View } from 'cadenza/workbook/view/view';
import { getLayerGroupsFromView, getViewById } from 'cadenza/workbook/workbook.state';

export function isReferenceLayer({ settings }: WorkbookLayerDto) {
  return Boolean(
    settings[LayerSettingsKey.toPersistenceKey('CATALOG_LAYER_REPOSITORY_NAME')] &&
      settings[LayerSettingsKey.toPersistenceKey('CATALOG_LAYER_ITEM_NAME')],
  );
}

/**
 * returns data views which are not referenced or indirectly referenced by having a parent group layer
 * which is a referenced layer
 */
export function getUnreferencedDataViews(view: View) {
  if (view.type !== 'MAP') {
    return view.dataViews;
  }
  const viewDto = getViewById(getStore().getState(), view.viewId);
  return view.dataViews.filter((dataView) => {
    const layer = viewDto.layerDefinitions[dataView.dataViewId];
    return layer && isUnreferencedLayer(layer, view.viewId);
  });
}

/**
 * returns data views which are not inside referenced groups.
 * referenced data views will be included
 */
export function getDataViewsNotWithinReferenceGroups(viewId: ViewId) {
  const view = getViewById(getStore().getState(), viewId);
  if (view.type !== 'MAP') {
    return view.dataViews;
  }
  return view.dataViews.filter((dataViewId) => {
    // filter out layers in reference groups
    return !isLayerInsideViewReferenceGroupLayer(viewId, dataViewId);
  });
}

/**
 * returns data views which are directly referenced,
 * meaning there are not referenced through a referenced parent group
 */
function getDirectReferencedDataViews(view: View) {
  if (view.type !== 'MAP') {
    return [];
  }
  const viewDto = getViewById(getStore().getState(), view.viewId);
  return view.dataViews.filter((dataView) => {
    const layer = viewDto.layerDefinitions[dataView.dataViewId];
    return isReferenceLayer(layer);
  });
}

export function getDataViewAnalysisContextDataViews(view: View) {
  return [...getUnreferencedDataViews(view), ...getDirectReferencedDataViews(view)];
}

/**
 * returns true if this layer is not referenced or indirectly referenced by having a parent group
 * layer which is a referenced layer
 */
export function isUnreferencedLayer(layer: WorkbookLayerDto, viewId: ViewId) {
  return !isReferenceLayer(layer) && !isLayerInsideViewReferenceGroupLayer(viewId, layer.id);
}

function isLayerInsideViewReferenceGroupLayer(viewId: ViewId, layerId: LayerId) {
  const viewDto = getViewById(getStore().getState(), viewId);
  return viewDto.layerTree.some((node) => isLayerInsideReferenceGroupLayer(viewDto, layerId, node));
}

function isLayerInsideReferenceGroupLayer(viewDto: ViewDto, layerId: LayerId, node: WorkbookLayerTreeNode): boolean {
  const nodeIsReferenceLayer = isReferenceLayer(viewDto.layerDefinitions[node.id]);
  if (nodeIsReferenceLayer) {
    return isLayerInsideNode(node, layerId);
  }
  return node.children.some((child) => isLayerInsideReferenceGroupLayer(viewDto, layerId, child));
}

export function getReferenceGroupsWithDataViews(viewId: ViewId) {
  if (getViewById(getStore().getState(), viewId).type !== 'MAP') {
    return [];
  }
  const groups = getLayerGroupsFromView(getStore().getState(), viewId);
  return groups.filter((group) => isReferenceLayer(group) && checkGroupForDataViews(viewId, group.id));
}

function checkGroupForDataViews(viewId: ViewId, layerId: LayerId) {
  const viewDto = getViewById(getStore().getState(), viewId);
  const node = viewDto.layerTree.find((layerTree) => layerTree.id === layerId);
  return node && isDataViewInNode(viewDto, node);
}

function isDataViewInNode(view: ViewDto, node: WorkbookLayerTreeNode): boolean {
  const layerDefinition = view.layerDefinitions[node.id];
  if (isDataViewLayer(layerDefinition)) {
    return true;
  }
  return node.children.some((layerTreeNode) => isDataViewInNode(view, layerTreeNode));
}

export function isReferenceLayerById(viewId: ViewId, layerId: LayerId) {
  const viewDto = getViewById(getStore().getState(), viewId);
  return isDataViewReferenceByViewDto(viewDto, layerId);
}

export function isDataViewReferenceByViewDto(viewDto: ViewDto, layerId: LayerId) {
  // layers can only be in map view
  if (viewDto.type !== 'MAP') {
    return false;
  }
  const layerFromDefinition = Object.values(viewDto.layerDefinitions).find((layer) => layer.id === layerId);
  return layerFromDefinition && isReferenceLayer(layerFromDefinition);
}

function isLayerInsideNode(parentLayer: WorkbookLayerTreeNode, layerId: LayerId): boolean {
  return parentLayer.children.some((child) => {
    return child.children.length > 0 ? isLayerInsideNode(child, layerId) : child.id === layerId;
  });
}

export function isOrContainsDataViewLayer(
  layerId: LayerId,
  layerTreeNode: WorkbookLayerTreeNode,
  layerDefinitions: Record<LayerId, WorkbookLayerDto>,
) {
  if (isDataViewLayer(layerDefinitions[layerId])) {
    return true;
  }
  for (const node of layerTreeNode.children) {
    if (isDataViewLayer(layerDefinitions[node.id])) {
      return true;
    }
    if (node.children.length) {
      return isOrContainsDataViewLayer(layerId, node, layerDefinitions);
    }
  }
  return false;
}

export function findNodeInTreeModel(
  nodes: WorkbookLayerTreeNode[],
  layerId: LayerId,
): WorkbookLayerTreeNode | undefined {
  for (const node of nodes) {
    if (node.id === layerId) {
      return node;
    }
    const foundNode = findNodeInTreeModel(node.children, layerId);
    if (foundNode) {
      return foundNode;
    }
  }
}

export function sortDataViewIdsBasedOnLayerTree(viewId: ViewId, dataViewIds: DataViewId[]): DataViewId[] {
  const view = getViewById(getStore().getState(), viewId);
  const layerTree = view.layerTree;
  if (!layerTree) {
    return dataViewIds;
  }
  return flatten(view.layerTree)
    .map((layerTreeNode) => layerTreeNode.id)
    .filter((layerId) => dataViewIds.includes(layerId as DataViewId))
    .map((layerId) => layerId as DataViewId);
}

function flatten(layerTreeNodes: WorkbookLayerTreeNode[]): WorkbookLayerTreeNode[] {
  return layerTreeNodes.flatMap((layerTreeNode) => [layerTreeNode, ...flatten(layerTreeNode.children)]);
}
